import React, { useState, useEffect, useCallback } from "react";
import {
  getRoles, getRol, getCatalogoFunciones, crearRol, actualizarRol, setFuncionesRol,
  agregarUsuarioARol, quitarUsuarioDeRol, getUsuariosContexto,
  getFuncionesDirectas, setFuncionesDirectas,
  RolListDto, RolDetailDto, FuncionCatalogoDto, UsuarioContextoListDto
} from "../../services/api";

interface Props {
  token: string;
  usuarioId: number;
  empresaActivaId: number | null;
}

type ModoPanel = "nuevo" | "editar" | "permisosDirectos" | null;
type Tab = "datos" | "funciones" | "usuarios";

const PANTALLA_ORDEN = ["dashboard", "grupo", "parametros", "contable", "rrhh"];
const PANTALLA_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  grupo: "Módulo Grupo",
  parametros: "Módulo Empresa",
  contable: "Módulo Contable",
  rrhh: "Módulo Recursos Humanos",
};
const OBJETO_ORDEN: Record<string, string[]> = {
  dashboard: ["modulo"],
  grupo: ["modulo", "general", "usuarios", "accesos"],
  parametros: ["modulo", "empresas", "usuarios", "accesos", "integraciones"],
  contable: ["modulo", "asientos", "planDeCuentas", "monedas", "balance", "resultados"],
  rrhh: ["modulo", "empleados", "liquidaciones", "ausencias"],
};
const OBJETO_LABELS: Record<string, string> = {
  modulo: "General del módulo",
  general: "General",
  empresas: "General",
  usuarios: "Usuarios",
  accesos: "Accesos y permisos",
  integraciones: "Apps integradas",
  asientos: "Asientos contables",
  planDeCuentas: "Plan de cuentas",
  monedas: "Monedas",
  balance: "Balance",
  resultados: "Estado de resultados",
  empleados: "Empleados",
  liquidaciones: "Liquidaciones",
  ausencias: "Ausencias",
};
const ACCION_ORDEN = ["ver", "agregar", "editar", "modificar", "eliminar"];
const ACCION_LABELS: Record<string, string> = {
  ver: "Ver", agregar: "Agregar", editar: "Editar", modificar: "Modificar", eliminar: "Eliminar",
};

function ordenarPorLista(valores: string[], orden: string[]): string[] {
  const enLista = orden.filter(o => valores.includes(o));
  const fueraDeLista = valores.filter(v => !orden.includes(v)).sort();
  return [...enLista, ...fueraDeLista];
}
function labelPantalla(p: string) { return PANTALLA_LABELS[p] || p; }
function labelObjeto(_: string, objeto: string) { return OBJETO_LABELS[objeto] || objeto; }
function labelAccion(a: string) { return ACCION_LABELS[a] || a; }

export default function AccesosPermisos({ token, usuarioId, empresaActivaId }: Props) {
  const [cargando, setCargando] = useState(true);
  const [autorizado, setAutorizado] = useState(true);
  const [roles, setRoles] = useState<RolListDto[]>([]);
  const [catalogo, setCatalogo] = useState<FuncionCatalogoDto[]>([]);
  const [modoPanel, setModoPanel] = useState<ModoPanel>(null);
  const [tab, setTab] = useState<Tab>("datos");
  const [detalle, setDetalle] = useState<RolDetailDto | null>(null);
  const [funcionIdsEditando, setFuncionIdsEditando] = useState<Set<number>>(new Set());
  const [formNuevo, setFormNuevo] = useState({ nombre: "", descripcion: "" });
  const [guardando, setGuardando] = useState(false);
  const [mensajeOk, setMensajeOk] = useState("");
  const [mensajeError, setMensajeError] = useState("");
  const [usuariosDisponibles, setUsuariosDisponibles] = useState<{ usuarioId: number; nombre: string; email: string }[]>([]);
  const [usuarioAAgregar, setUsuarioAAgregar] = useState(0);

  // ===== Permisos directos =====
  const [usuariosPD, setUsuariosPD] = useState<UsuarioContextoListDto[]>([]);
  const [usuarioPDSeleccionado, setUsuarioPDSeleccionado] = useState<UsuarioContextoListDto | null>(null);
  const [funcionIdsPD, setFuncionIdsPD] = useState<Set<number>>(new Set());
  const [cargandoPD, setCargandoPD] = useState(false);

  const cargarRoles = useCallback(async () => {
    if (!empresaActivaId) { setCargando(false); return; }
    const data = await getRoles(empresaActivaId, usuarioId, token);
    if (data === null) { setAutorizado(false); } else { setAutorizado(true); setRoles(data); }
    setCargando(false);
  }, [empresaActivaId, usuarioId, token]);

  useEffect(() => { cargarRoles(); }, [cargarRoles]);
  useEffect(() => { getCatalogoFunciones(token).then(setCatalogo); }, [token]);

  // Cargar usuarios de la empresa para el panel de permisos directos
  const cargarUsuariosPD = useCallback(async () => {
    if (!empresaActivaId) return;
    const ctx = await getUsuariosContexto(usuarioId, { empresaId: empresaActivaId, estado: "activos" }, token);
    setUsuariosPD(ctx?.usuarios || []);
  }, [empresaActivaId, usuarioId, token]);

  const cerrarPanel = () => {
    setModoPanel(null);
    setDetalle(null);
    setMensajeOk("");
    setMensajeError("");
    setUsuarioAAgregar(0);
    setUsuarioPDSeleccionado(null);
    setFuncionIdsPD(new Set());
  };

  // ===== Roles =====

  const handleNuevo = () => {
    setFormNuevo({ nombre: "", descripcion: "" });
    setModoPanel("nuevo");
    setTab("datos");
    setMensajeOk(""); setMensajeError("");
  };

  const handleCrear = async () => {
    if (!empresaActivaId || !formNuevo.nombre.trim()) { setMensajeError("El nombre es obligatorio"); return; }
    setGuardando(true);
    const r = await crearRol(
      { solicitanteId: usuarioId, empresaId: empresaActivaId, nombre: formNuevo.nombre, descripcion: formNuevo.descripcion || undefined },
      token
    );
    if (r.ok && r.rol) { await cargarRoles(); await abrirEditar(r.rol.rolId); }
    else { setMensajeError(r.mensaje || "Error al crear el rol"); }
    setGuardando(false);
  };

  const abrirEditar = async (rolId: number) => {
    const d = await getRol(rolId, usuarioId, token);
    if (!d) { setMensajeError("No se pudo cargar el rol"); return; }
    setDetalle(d);
    setFuncionIdsEditando(new Set(d.funcionIds));
    setModoPanel("editar");
    setTab("datos");
    setMensajeOk(""); setMensajeError("");
    if (empresaActivaId) {
      const ctx = await getUsuariosContexto(usuarioId, { empresaId: empresaActivaId, estado: "activos" }, token);
      const activos = ctx?.usuarios || [];
      setUsuariosDisponibles(activos.filter(u => !d.usuarios.some(du => du.usuarioId === u.usuarioId))
        .map(u => ({ usuarioId: u.usuarioId, nombre: u.nombre, email: u.email })));
    }
  };

  const handleGuardarDatos = async () => {
    if (!detalle) return;
    setGuardando(true);
    const ok = await actualizarRol(detalle.rolId,
      { solicitanteId: usuarioId, nombre: detalle.nombre, descripcion: detalle.descripcion || undefined, activo: detalle.activo }, token);
    if (ok) { setMensajeOk("Guardado"); await cargarRoles(); }
    else { setMensajeError("Error al guardar"); }
    setGuardando(false);
  };

  const toggleFuncion = (funcionId: number) => {
    setFuncionIdsEditando(prev => { const next = new Set(prev); if (next.has(funcionId)) next.delete(funcionId); else next.add(funcionId); return next; });
  };

  const handleGuardarFunciones = async () => {
    if (!detalle) return;
    setGuardando(true);
    const ok = await setFuncionesRol(detalle.rolId, usuarioId, Array.from(funcionIdsEditando), token);
    if (ok) { setMensajeOk("Funciones actualizadas"); await cargarRoles(); }
    else { setMensajeError("Error al guardar las funciones"); }
    setGuardando(false);
  };

  const handleAgregarUsuario = async () => {
    if (!detalle || !usuarioAAgregar) return;
    const ok = await agregarUsuarioARol(detalle.rolId, usuarioAAgregar, usuarioId, token);
    if (ok) { await abrirEditar(detalle.rolId); setUsuarioAAgregar(0); await cargarRoles(); }
    else { setMensajeError("Error al agregar el usuario"); }
  };

  const handleQuitarUsuario = async (uid: number) => {
    if (!detalle) return;
    const ok = await quitarUsuarioDeRol(detalle.rolId, uid, usuarioId, token);
    if (ok) { await abrirEditar(detalle.rolId); await cargarRoles(); }
    else { setMensajeError("Error al quitar el usuario"); }
  };

  // ===== Permisos directos =====

  const abrirPermisosDirectos = async () => {
    setModoPanel("permisosDirectos");
    setUsuarioPDSeleccionado(null);
    setFuncionIdsPD(new Set());
    setMensajeOk(""); setMensajeError("");
    await cargarUsuariosPD();
  };

  const handleSeleccionarUsuarioPD = async (u: UsuarioContextoListDto) => {
    if (!empresaActivaId) return;
    setCargandoPD(true);
    setUsuarioPDSeleccionado(u);
    const ids = await getFuncionesDirectas(u.usuarioId, empresaActivaId, usuarioId, token);
    setFuncionIdsPD(new Set(ids));
    setCargandoPD(false);
  };

  const toggleFuncionPD = (funcionId: number) => {
    setFuncionIdsPD(prev => { const next = new Set(prev); if (next.has(funcionId)) next.delete(funcionId); else next.add(funcionId); return next; });
  };

  const handleGuardarFuncionesPD = async () => {
    if (!usuarioPDSeleccionado || !empresaActivaId) return;
    setGuardando(true);
    const ok = await setFuncionesDirectas(usuarioPDSeleccionado.usuarioId, empresaActivaId, usuarioId, Array.from(funcionIdsPD), token);
    if (ok) { setMensajeOk("Permisos directos actualizados"); }
    else { setMensajeError("Error al guardar"); }
    setGuardando(false);
  };

  if (cargando) return <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Cargando roles...</div>;

  if (!autorizado) {
    return (
      <div style={styles.container}>
        <div style={styles.mensajeErrorGlobal}>No tenés permisos de administración en este contexto.</div>
      </div>
    );
  }

  const porPantalla: Record<string, Record<string, FuncionCatalogoDto[]>> = {};
  catalogo.forEach(f => {
    if (!porPantalla[f.pantalla]) porPantalla[f.pantalla] = {};
    if (!porPantalla[f.pantalla][f.objeto]) porPantalla[f.pantalla][f.objeto] = [];
    porPantalla[f.pantalla][f.objeto].push(f);
  });
  const pantallasOrdenadas = ordenarPorLista(Object.keys(porPantalla), PANTALLA_ORDEN);

  const renderCatalogoFunciones = (
    ids: Set<number>,
    toggle: (id: number) => void
  ) => (
    <div style={{ maxHeight: 420, overflowY: "auto" }}>
      {pantallasOrdenadas.map(pantalla => {
        const objetos = ordenarPorLista(Object.keys(porPantalla[pantalla]), OBJETO_ORDEN[pantalla] || []);
        return (
          <div key={pantalla} style={{ marginBottom: 18 }}>
            <div style={styles.moduloTitulo}>{labelPantalla(pantalla)}</div>
            {objetos.map(objeto => {
              const funciones = [...porPantalla[pantalla][objeto]].sort((a, b) => {
                const oa = ACCION_ORDEN.indexOf(a.accion);
                const ob = ACCION_ORDEN.indexOf(b.accion);
                if (oa !== -1 && ob !== -1) return oa - ob;
                if (oa !== -1) return -1;
                if (ob !== -1) return 1;
                return a.accion.localeCompare(b.accion);
              });
              return (
                <div key={objeto} style={{ marginBottom: 10, marginLeft: 8 }}>
                  <div style={styles.pantallaTitulo}>{labelObjeto(pantalla, objeto)}</div>
                  {funciones.map(f => (
                    <label key={f.funcionId} style={styles.funcionRow}>
                      <input type="checkbox" checked={ids.has(f.funcionId)} onChange={() => toggle(f.funcionId)} />
                      <span>
                        <strong>{labelAccion(f.accion)}</strong>
                        {f.descripcion && <span style={{ color: "#999" }}> — {f.descripcion}</span>}
                      </span>
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
      {/* TABLA PRINCIPAL */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Roles */}
        <div style={styles.container}>
          <div style={styles.toolbar}>
            <span style={styles.titulo}>Roles</span>
            <button style={styles.btnNuevo} onClick={handleNuevo}>+ Nuevo rol</button>
          </div>
          <div style={styles.encabezado}>
            <div style={{ ...styles.colH, flex: 1 }}>Nombre</div>
            <div style={{ ...styles.colH, flex: 2 }}>Descripción</div>
            <div style={{ ...styles.colH, width: 90, textAlign: "center" }}>Funciones</div>
            <div style={{ ...styles.colH, width: 90, textAlign: "center" }}>Usuarios</div>
            <div style={{ ...styles.colH, width: 90, textAlign: "center" }}>Activo</div>
            <div style={{ ...styles.colH, width: 90, textAlign: "center" }}>Acciones</div>
          </div>
          <div style={styles.tabla}>
            {roles.length === 0 && <div style={styles.vacio}>No hay roles creados todavía</div>}
            {roles.map(r => (
              <div key={r.rolId} style={{ ...styles.fila, opacity: r.activo ? 1 : 0.55 }}>
                <div style={{ ...styles.col, flex: 1, fontWeight: 600 }}>{r.nombre}</div>
                <div style={{ ...styles.col, flex: 2, color: "#888" }}>{r.descripcion || "—"}</div>
                <div style={{ ...styles.col, width: 90, justifyContent: "center" }}>{r.cantidadFunciones}</div>
                <div style={{ ...styles.col, width: 90, justifyContent: "center" }}>{r.cantidadUsuarios}</div>
                <div style={{ ...styles.col, width: 90, justifyContent: "center" }}>{r.activo ? "✓" : "—"}</div>
                <div style={{ ...styles.col, width: 90, justifyContent: "center" }}>
                  <button style={styles.btnAccion} onClick={() => abrirEditar(r.rolId)}>Editar</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Permisos directos por usuario */}
        <div style={styles.container}>
          <div style={styles.toolbar}>
            <span style={styles.titulo}>Permisos directos por usuario</span>
            <button style={styles.btnSecundario} onClick={abrirPermisosDirectos}>Gestionar</button>
          </div>
          <div style={{ padding: "12px 20px" }}>
            <p style={{ fontSize: 13, color: "#888", margin: 0 }}>
              Asigná funciones puntuales directamente a un usuario, sin necesidad de crear un Rol.
              Se suman a los que ya tiene por sus Roles asignados.
            </p>
          </div>
        </div>
      </div>

      {/* PANEL: nuevo rol */}
      {modoPanel === "nuevo" && (
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span style={styles.panelTitulo}>Nuevo Rol</span>
            <button style={styles.panelCerrar} onClick={cerrarPanel}>✕</button>
          </div>
          <div style={styles.campo}>
            <label style={styles.campoLabel}>Nombre</label>
            <input style={styles.campoInput} value={formNuevo.nombre}
              onChange={e => setFormNuevo({ ...formNuevo, nombre: e.target.value })} placeholder="Ej: Contador" />
          </div>
          <div style={styles.campo}>
            <label style={styles.campoLabel}>Descripción</label>
            <input style={styles.campoInput} value={formNuevo.descripcion}
              onChange={e => setFormNuevo({ ...formNuevo, descripcion: e.target.value })} />
          </div>
          {mensajeError && <div style={styles.mensajeError}>✕ {mensajeError}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button style={styles.btnGuardar} onClick={handleCrear} disabled={guardando}>
              {guardando ? "Creando..." : "Crear"}
            </button>
            <button style={styles.btnCancelar} onClick={cerrarPanel}>Cancelar</button>
          </div>
        </div>
      )}

      {/* PANEL: editar rol */}
      {modoPanel === "editar" && detalle && (
        <div style={{ ...styles.panel, width: 440 }}>
          <div style={styles.panelHeader}>
            <span style={styles.panelTitulo}>Rol — {detalle.nombre}</span>
            <button style={styles.panelCerrar} onClick={cerrarPanel}>✕</button>
          </div>
          <div style={styles.solapas}>
            {(["datos", "funciones", "usuarios"] as Tab[]).map(t => (
              <button key={t} style={{ ...styles.solapa, ...(tab === t ? styles.solapaActiva : {}) }}
                onClick={() => setTab(t)}>
                {t === "datos" ? "Datos" : t === "funciones" ? "Funciones" : "Usuarios"}
              </button>
            ))}
          </div>

          {tab === "datos" && (
            <div>
              <div style={styles.campo}>
                <label style={styles.campoLabel}>Nombre</label>
                <input style={styles.campoInput} value={detalle.nombre}
                  onChange={e => setDetalle({ ...detalle, nombre: e.target.value })} />
              </div>
              <div style={styles.campo}>
                <label style={styles.campoLabel}>Descripción</label>
                <input style={styles.campoInput} value={detalle.descripcion || ""}
                  onChange={e => setDetalle({ ...detalle, descripcion: e.target.value })} />
              </div>
              <div style={styles.campoCheckbox}>
                <input type="checkbox" checked={detalle.activo}
                  onChange={e => setDetalle({ ...detalle, activo: e.target.checked })} />
                <span>Activo</span>
              </div>
              <button style={styles.btnGuardar} onClick={handleGuardarDatos} disabled={guardando}>
                {guardando ? "Guardando..." : "Guardar datos"}
              </button>
            </div>
          )}

          {tab === "funciones" && (
            <>
              {renderCatalogoFunciones(funcionIdsEditando, toggleFuncion)}
              <button style={styles.btnGuardar} onClick={handleGuardarFunciones} disabled={guardando}>
                {guardando ? "Guardando..." : "Guardar funciones"}
              </button>
            </>
          )}

          {tab === "usuarios" && (
            <div>
              {usuariosDisponibles.length > 0 && (
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  <select style={{ ...styles.campoInput, flex: 1 }}
                    value={usuarioAAgregar}
                    onChange={e => setUsuarioAAgregar(Number(e.target.value))}>
                    <option value={0}>— Elegir usuario —</option>
                    {usuariosDisponibles.map(u => (
                      <option key={u.usuarioId} value={u.usuarioId}>{u.nombre} ({u.email})</option>
                    ))}
                  </select>
                  <button style={styles.btnNuevo} onClick={handleAgregarUsuario}>+</button>
                </div>
              )}
              {detalle.usuarios.length === 0 && <div style={styles.vacio}>Sin usuarios asignados</div>}
              {detalle.usuarios.map(u => (
                <div key={u.usuarioId} style={styles.filaAsignacion}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{u.nombre}</div>
                    <div style={{ fontSize: 11, color: "#999" }}>{u.email}</div>
                  </div>
                  <button style={{ ...styles.btnAccion, color: "#e74c3c", border: "1px solid #e74c3c" }}
                    onClick={() => handleQuitarUsuario(u.usuarioId)}>✕</button>
                </div>
              ))}
            </div>
          )}

          {mensajeOk && <div style={styles.mensajeOk}>✓ {mensajeOk}</div>}
          {mensajeError && <div style={styles.mensajeError}>✕ {mensajeError}</div>}
        </div>
      )}

      {/* PANEL: permisos directos */}
      {modoPanel === "permisosDirectos" && (
        <div style={{ ...styles.panel, width: 460 }}>
          <div style={styles.panelHeader}>
            <span style={styles.panelTitulo}>Permisos directos por usuario</span>
            <button style={styles.panelCerrar} onClick={cerrarPanel}>✕</button>
          </div>

          {/* Selector de usuario */}
          <div style={styles.campo}>
            <label style={styles.campoLabel}>Usuario</label>
            <select style={styles.campoInput}
              value={usuarioPDSeleccionado?.usuarioId ?? 0}
              onChange={e => {
                const uid = Number(e.target.value);
                const u = usuariosPD.find(x => x.usuarioId === uid);
                if (u) handleSeleccionarUsuarioPD(u);
                else { setUsuarioPDSeleccionado(null); setFuncionIdsPD(new Set()); }
              }}>
              <option value={0}>— Elegir usuario —</option>
              {usuariosPD.map(u => (
                <option key={u.usuarioId} value={u.usuarioId}>{u.nombre} ({u.email})</option>
              ))}
            </select>
          </div>

          {cargandoPD && <div style={{ textAlign: "center", color: "#888", padding: 20 }}>Cargando...</div>}

          {usuarioPDSeleccionado && !cargandoPD && (
            <>
              <p style={styles.notaInfo}>
                Estas funciones se suman a las que ya tiene por sus Roles. El conjunto final es la unión de ambas.
              </p>
              {renderCatalogoFunciones(funcionIdsPD, toggleFuncionPD)}
              <button style={styles.btnGuardar} onClick={handleGuardarFuncionesPD} disabled={guardando}>
                {guardando ? "Guardando..." : "Guardar permisos directos"}
              </button>
            </>
          )}

          {mensajeOk && <div style={styles.mensajeOk}>✓ {mensajeOk}</div>}
          {mensajeError && <div style={styles.mensajeError}>✕ {mensajeError}</div>}
        </div>
      )}
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" },
  toolbar: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid #f0f0f0" },
  titulo: { fontSize: 15, fontWeight: 700, color: "#1a1a2e" },
  btnNuevo: { padding: "8px 16px", backgroundColor: "#2B8FCC", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnSecundario: { padding: "8px 16px", backgroundColor: "#fff", color: "#2B8FCC", border: "1px solid #2B8FCC", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  encabezado: { display: "flex", alignItems: "center", padding: "8px 16px", backgroundColor: "#f4f6f8", borderBottom: "2px solid #e0e0e0" },
  colH: { fontSize: 11, fontWeight: 700, color: "#999", letterSpacing: "0.5px", textTransform: "uppercase" as const, padding: "0 8px" },
  tabla: { overflowY: "auto" as const, maxHeight: "calc(100vh - 400px)" },
  fila: { display: "flex", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid #f5f5f5", fontSize: 13 },
  col: { display: "flex", alignItems: "center", padding: "0 8px" },
  vacio: { padding: 40, textAlign: "center" as const, color: "#bbb", fontSize: 14 },
  btnAccion: { padding: "4px 10px", backgroundColor: "#f4f6f8", color: "#555", border: "1px solid #ddd", borderRadius: 6, fontSize: 12, cursor: "pointer" },
  mensajeErrorGlobal: { backgroundColor: "#fff0f0", color: "#e74c3c", padding: "16px 20px", fontSize: 13 },
  panel: { width: 360, backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.1)", padding: 20, flexShrink: 0 },
  panelHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  panelTitulo: { fontSize: 14, fontWeight: 700, color: "#1a1a2e" },
  panelCerrar: { background: "transparent", border: "none", cursor: "pointer", fontSize: 16, color: "#aaa" },
  solapas: { display: "flex", borderBottom: "2px solid #e0e0e0", marginBottom: 16 },
  solapa: { padding: "7px 14px", border: "none", background: "transparent", fontSize: 13, cursor: "pointer", color: "#888", borderBottom: "2px solid transparent", marginBottom: -2 },
  solapaActiva: { color: "#2B8FCC", fontWeight: 600, borderBottom: "2px solid #2B8FCC" },
  campo: { marginBottom: 12 },
  campoLabel: { display: "block", fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase" as const, letterSpacing: "0.5px", marginBottom: 4 },
  campoInput: { width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", fontSize: 13, boxSizing: "border-box" as const, outline: "none" },
  campoCheckbox: { display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 13, color: "#333" },
  moduloTitulo: { fontSize: 13, fontWeight: 700, color: "#1a1a2e", backgroundColor: "#f4f6f8", padding: "6px 10px", borderRadius: 6, marginBottom: 8 },
  pantallaTitulo: { fontSize: 11, fontWeight: 700, color: "#2B8FCC", textTransform: "uppercase" as const, letterSpacing: "0.5px", marginBottom: 4, borderBottom: "1px solid #f0f0f0", paddingBottom: 3 },
  funcionRow: { display: "flex", alignItems: "flex-start", gap: 8, padding: "4px 0 4px 8px", fontSize: 13, cursor: "pointer" },
  filaAsignacion: { display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid #f5f5f5", fontSize: 13 },
  notaInfo: { fontSize: 12, color: "#888", backgroundColor: "#f8f9fa", padding: "8px 10px", borderRadius: 8, marginBottom: 12 },
  btnGuardar: { width: "100%", padding: 10, backgroundColor: "#2B8FCC", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", marginTop: 8 },
  btnCancelar: { flex: 1, padding: 10, backgroundColor: "#f4f6f8", color: "#555", border: "1px solid #ddd", borderRadius: 8, fontSize: 13, cursor: "pointer" },
  mensajeOk: { backgroundColor: "#e8f8f0", color: "#27ae60", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginTop: 8, textAlign: "center" as const },
  mensajeError: { backgroundColor: "#fff0f0", color: "#e74c3c", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginTop: 8, textAlign: "center" as const },
};